import { useState } from "react";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import Label from "../../../../components/form_fields/Label";
import { SubmitHandler, useForm } from "react-hook-form";
import {
	useCreateBonus,
	useUpdateBonus,
} from "../../../../services/bonus.service";
import {
	IAddEditTableForm,
	manageResourceIds,
	formatRate,
} from "../../../../helpers/appHelpers";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import FormDescriptionInput from "../../../../components/form_fields/FormDescriptionInput";
import MoneyInput from "../../../../components/form_fields/MoneyInput";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import { useManageTableWithSearchParamsData } from "../../../../helpers/useManageTable";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import { PayPeriodDto } from "../../../../models/pay-period.dto";
import { EmployeeBandDto } from "../../../../models/employee-band.dto";
import FormCodeInput from "../../../../components/form_fields/FormCodeInput";
import MultiSelectField from "../../../../components/form_fields/MultiSelectField";
import { PATHS } from "../../../../routes/routes.paths";
import RateComponent from "../../../../components/form_fields/RateComponent";
import { EmployeeDto } from "../../../../models/employee.dto";
import APISelectField from "../../../../components/form_fields/APISelectField";
import { BonusGroupDto } from "../../../../models/bonus-group.dto";
import { filterPayloadData } from "../../../../services/utils/requests";
import {
	EMPLOYMENT_PERIOD_UNIT,
	PAYMENT_FREQUENCY,
} from "../../../../models/bonus.dto";
import { CompanyCurrencyConfigDto } from "../../../../models/company-currency.dto";

interface IFormInputs {
	code: string;
	name: string;
	description: string;
	fixedComponent: number;
	rateComponent: number;
	active: string;
	prorate: boolean;
	periodStartDate: string;
	periodEndDate: string;
	minValue: number;
	currencyId: number;
	paymentFrequency: PAYMENT_FREQUENCY;
	employmentPeriodUnit: EMPLOYMENT_PERIOD_UNIT;
	minEmploymentPeriod?: number;
	maxValue?: number;
	employeeIds?: number[];
	employeeBandIds?: number[];
	payPeriodIds?: number[];
}

type EmployeeBonusModalFormProps = IAddEditTableForm & {
	employees: EmployeeDto[];
	employeeBands: EmployeeBandDto[];
	payPeriods: PayPeriodDto[];
	bonusGroups: BonusGroupDto[];
	currencies: CompanyCurrencyConfigDto[];
	reloadTableData?: () => void;
};

const EmployeeBonusModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	employees,
	employeeBands,
	payPeriods,
	bonusGroups,
	currencies,
	reloadTableData,
}: EmployeeBonusModalFormProps) => {
	const { currentCompany } = useCompanyStore();
	const { formDataValues } = useEditFormStore();
	const [uploading, setUploading] = useState(false);

	const {
		register,
		watch,
		handleSubmit,
		setValue,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			name: isEditForm ? formDataValues?.name : "",
			code: isEditForm ? formDataValues?.code : "",
			description: isEditForm ? formDataValues?.description : "",
			fixedComponent: isEditForm ? formDataValues?.fixedComponent : "",
			rateComponent: isEditForm
				? ((Number(formDataValues?.rateComponent) * 100) as any)
				: "",
			active: isEditForm ? (formDataValues?.active ? "yes" : "no") : "",
			prorate: isEditForm ? formDataValues?.prorate : false,
			periodStartDate: isEditForm
				? formDataValues?.periodStartDate?.split("T")[0]
				: "",
			periodEndDate: isEditForm
				? formDataValues?.periodEndDate?.split("T")[0]
				: "",
			minValue: isEditForm ? formDataValues?.minValue : "",
			maxValue: isEditForm ? formDataValues?.maxValue : "",
			currencyId: isEditForm ? formDataValues?.currencyId : "",
			paymentFrequency: isEditForm ? formDataValues?.paymentFrequency : "",
			employmentPeriodUnit: isEditForm
				? formDataValues?.employmentPeriodUnit
				: "",
			minEmploymentPeriod: isEditForm
				? formDataValues?.minEmploymentPeriod
				: "",
		},
	});

	const [employeeIds, setEmployeeIds] = useState<number[]>([]);
	const [employeeBandIds, setEmployeeBandIds] = useState<number[]>([]);
	const [payPeriodIds, setPayPeriodIds] = useState<number[]>([]);

	const { mutate: createBonus } = useCreateBonus();
	const { mutate: updateBonus } = useUpdateBonus();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
		setUploading(true);

		formatRate(data);
		data.active = data.active === "yes" ? true : false;
		data.prorate = data.prorate === "true" || data.prorate === true;

		if (isEditForm) {
			const { addIds: addPayPeriodIds, removeIds: removePayPeriodIds } =
				manageResourceIds({
					newData: payPeriodIds,
					oldData:
						formDataValues?.payPeriods?.map((payPeriod) => payPeriod.id) || [],
				});
			const { addIds: addEmployeeIds, removeIds: removeEmployeeIds } =
				manageResourceIds({
					newData: employeeIds,
					oldData:
						formDataValues?.employeeBonuses?.map(
							(employeeBonus) => employeeBonus?.employee.id,
						) || [],
				});
			const { addIds: addEmployeeBandIds, removeIds: removeEmployeeBandIds } =
				manageResourceIds({
					newData: employeeBandIds,
					oldData:
						formDataValues?.employeeBandBonuses?.map(
							(employeeBandBonus) => employeeBandBonus?.employeeBand.id,
						) || [],
				});

			data.addPayPeriodIds = addPayPeriodIds;
			data.removePayPeriodIds = removePayPeriodIds;
			data.addEmployeeIds = addEmployeeIds;
			data.removeEmployeeIds = removeEmployeeIds;
			data.addEmployeeBandIds = addEmployeeBandIds;
			data.removeEmployeeBandIds = removeEmployeeBandIds;

			filterPayloadData(data);

			updateBonus(
				{ payload: data, id: formDataValues?.id },
				{
					onSuccess: (updatedData) => {
						try {
							tableAPIRef?.current.updateRows([updatedData.data]);
						} catch (error) {
							console.error("Error updating table rows:", error);
							reloadTableData?.();
						}
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			data.employeeIds = employeeIds.length ? (employeeIds as any) : undefined;
			data.employeeBandIds = employeeBandIds.length
				? (employeeBandIds as any)
				: undefined;
			data.payPeriodIds = payPeriodIds.length
				? (payPeriodIds as any)
				: undefined;

			createBonus(
				{ payload: { ...data, companyId: currentCompany?.id } },
				{
					onSuccess: () => {
						addNewTableData();
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	return (
		<ModalComponent open={open} title={title} handleClose={handleClose}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="grid grid-cols-2 gap-x-4 gap-y-6">
					<div className="w-full">
						<Label title="Name" for="name" />
						<input
							defaultValue={isEditForm ? formDataValues?.name : ""}
							placeholder={"Enter name"}
							type="text"
							{...register("name", {
								required: "Enter name here",
							})}
							id="name"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.name
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.name && (
							<p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Unique Code" for="code" />
						<FormCodeInput
							register={register}
							attributes={{
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.code
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
						/>
						{errors.code && (
							<p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
						)}
					</div>
					<div className="w-full col-span-2">
						<Label title="Description" for="description" />
						<FormDescriptionInput
							register={register}
							attributes={{
								defaultValue: isEditForm ? formDataValues?.description : "",
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.description
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
						/>
						{errors.description && (
							<p className="text-red-500 text-sm mt-1">
								{errors.description.message}
							</p>
						)}
					</div>

					<div className="w-full">
						<Label title="Currency" for="currencyId" />
						<select
							{...register("currencyId", {
								required: "Select currency",
								valueAsNumber: true,
							})}
							id="currencyId"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								errors.currencyId
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						>
							<option value="">Select Currency</option>
							{currencies?.map((item) => (
								<option key={item?.id} value={item?.id}>
									{item?.currency?.name} ({item?.currency?.code})
								</option>
							))}
						</select>
						{errors.currencyId && (
							<p className="text-red-500 text-sm mt-1">
								{errors.currencyId.message}
							</p>
						)}
					</div>

					<div className="w-full">
						<Label title="Period Start Date" for="periodStartDate" />
						<input
							type="date"
							defaultValue={
								isEditForm ? formDataValues?.periodStartDate?.split("T")[0] : ""
							}
							{...register("periodStartDate", {
								required: "Select start date",
							})}
							id="periodStartDate"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								errors.periodStartDate
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.periodStartDate && (
							<p className="text-red-500 text-sm mt-1">
								{errors.periodStartDate.message}
							</p>
						)}
					</div>

					<div className="w-full">
						<Label title="Period End Date" for="periodEndDate" />
						<input
							type="date"
							defaultValue={
								isEditForm ? formDataValues?.periodEndDate?.split("T")[0] : ""
							}
							{...register("periodEndDate", {
								required: "Select end date",
							})}
							id="periodEndDate"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								errors.periodEndDate
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.periodEndDate && (
							<p className="text-red-500 text-sm mt-1">
								{errors.periodEndDate.message}
							</p>
						)}
					</div>

					<div className="w-full">
						<Label title="Minimum Value" for="minValue" />
						<MoneyInput
							attributes={{
								id: "minValue",
								placeholder: "0",
							}}
							register={register}
							name="minValue"
							setValue={setValue}
							defaultValue={isEditForm ? formDataValues?.minValue : undefined}
							required="Enter minimum value"
							error={errors.minValue}
						/>
						{errors.minValue && (
							<p className="text-red-500 text-sm mt-1">
								{errors.minValue.message}
							</p>
						)}
					</div>

					<div className="w-full">
						<Label title="Maximum Value" for="maxValue" optional />
						<MoneyInput
							attributes={{
								id: "maxValue",
								placeholder: "0",
							}}
							register={register}
							name="maxValue"
							setValue={setValue}
							defaultValue={isEditForm ? formDataValues?.maxValue : undefined}
							error={errors.maxValue}
							required={""}
						/>
						{errors.maxValue && (
							<p className="text-red-500 text-sm mt-1">
								{errors.maxValue.message}
							</p>
						)}
					</div>

					<div className="w-full">
						<Label title="Payment Frequency" for="paymentFrequency" />
						<select
							{...register("paymentFrequency", {
								required: "Select payment frequency",
							})}
							id="paymentFrequency"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								errors.paymentFrequency
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						>
							<option value="">Select Frequency</option>
							<option value="DAILY">Daily</option>
							<option value="WEEKLY">Weekly</option>
							<option value="FORTNIGHTLY">Fortnightly</option>
							<option value="MONTHLY">Monthly</option>
							<option value="QUARTERLY">Quarterly</option>
							<option value="BIANNUALLY">Biannually</option>
							<option value="ANNUALLY">Annually</option>
							<option value="PRESET">Preset</option>
						</select>
						{errors.paymentFrequency && (
							<p className="text-red-500 text-sm mt-1">
								{errors.paymentFrequency.message}
							</p>
						)}
					</div>

					<div className="w-full">
						<Label title="Employment Period Unit" for="employmentPeriodUnit" />
						<select
							{...register("employmentPeriodUnit", {
								required: "Select employment period unit",
							})}
							id="employmentPeriodUnit"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								errors.employmentPeriodUnit
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						>
							<option value="">Select Unit</option>
							<option value="DAY">Day</option>
							<option value="MONTH">Month</option>
							<option value="YEAR">Year</option>
						</select>
						{errors.employmentPeriodUnit && (
							<p className="text-red-500 text-sm mt-1">
								{errors.employmentPeriodUnit.message}
							</p>
						)}
					</div>

					<div className="w-full">
						<Label
							title="Minimum Employment Period"
							for="minEmploymentPeriod"
							optional
						/>
						<input
							type="number"
							{...register("minEmploymentPeriod", {
								valueAsNumber: true,
							})}
							id="minEmploymentPeriod"
							placeholder="0"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								errors.minEmploymentPeriod
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.minEmploymentPeriod && (
							<p className="text-red-500 text-sm mt-1">
								{errors.minEmploymentPeriod.message}
							</p>
						)}
					</div>

					<div className="w-full col-span-2">
						<div className="flex space-x-12 text-base">
							<h3>Prorate</h3>
							<div className="flex space-x-3 items-center">
								<input
									{...register("prorate")}
									defaultChecked={
										isEditForm && formDataValues?.prorate === true
									}
									type="radio"
									value="true"
									id="prorate-yes"
								/>
								<label htmlFor="prorate-yes">Yes</label>
							</div>
							<div className="flex space-x-3 items-center">
								<input
									{...register("prorate")}
									defaultChecked={
										isEditForm && formDataValues?.prorate === false
									}
									type="radio"
									value="false"
									id="prorate-no"
								/>
								<label htmlFor="prorate-no">No</label>
							</div>
						</div>
					</div>
					<div className="w-full">
						<Label title="Fixed Component" for="fixedComponent" />
						<MoneyInput
							attributes={{
								id: "fixedComponent",
								placeholder: "0",
							}}
							register={register}
							name="fixedComponent"
							setValue={setValue}
							defaultValue={
								isEditForm ? formDataValues?.fixedComponent : undefined
							}
							required="Enter fixed component here"
							error={errors.fixedComponent}
						/>
						{errors.fixedComponent && (
							<p className="text-red-500 text-sm mt-1">
								{errors.fixedComponent.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Rate Component" for="rateComponent" />
						<RateComponent
							attributes={{
								id: "rateComponent",
							}}
							register={register}
							name="rateComponent"
							required="Enter rate component"
							error={errors.rateComponent}
						/>
						{errors.rateComponent && (
							<p className="text-red-500 text-sm mt-1">
								{errors.rateComponent.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Assign Pay Periods" for="payPeriodIds" optional />
						<MultiSelectField
							key={"payPeriodIds"}
							options={payPeriods}
							setResourceIds={setPayPeriodIds}
							placeholder={"Select Pay Periods"}
							pathname={PATHS.SUB_COMPANY_SETTINGS.PAY_PERIODS}
							mapText={"code"}
							required={false}
							defaultValues={
								!isEditForm
									? []
									: formDataValues?.payPeriods?.map(
											(payPeriod) => payPeriod.id,
										) || []
							}
						/>
					</div>
					<div className="w-full">
						<Label title="Assign Employees" for="employeeIds" optional />
						<MultiSelectField
							key={"employeeIds"}
							options={employees}
							setResourceIds={setEmployeeIds}
							placeholder={"Select Employees"}
							pathname={PATHS.EMPLOYEES}
							required={false}
							mapTexts={["firstName", "lastName"]}
							defaultValues={
								!isEditForm
									? []
									: formDataValues?.employeeBonuses?.map(
											(employeeBonus) => employeeBonus?.employee.id,
										) || []
							}
						/>
					</div>
					<div className="w-full">
						<Label
							title="Assign Employee Bands"
							for="employeeBandIds"
							optional
						/>
						<MultiSelectField
							key={"employeeBandIds"}
							options={employeeBands}
							setResourceIds={setEmployeeBandIds}
							placeholder={"Select Bands"}
							pathname={PATHS.SUB_COMPANY_SETTINGS.BANDS}
							required={false}
							defaultValues={
								!isEditForm
									? []
									: formDataValues?.employeeBandBonuses?.map(
											(employeeBandBonus) => employeeBandBonus?.employeeBand.id,
										) || []
							}
						/>
					</div>
					<div className="col-span-2">
						<div className="flex space-x-12 text-base">
							<h3>Active</h3>
							<div className="flex space-x-3 items-center">
								<input
									{...register("active")}
									defaultChecked={isEditForm && formDataValues?.active === true}
									type="radio"
									value={"yes"}
									id="active"
								/>
								<label htmlFor="active">Yes</label>
							</div>
							<div className="flex space-x-3 items-center">
								<input
									{...register("active")}
									defaultChecked={
										isEditForm && formDataValues?.active === false
									}
									type="radio"
									value={"no"}
									id="inactive"
								/>
								<label htmlFor="inactive">No</label>
							</div>
						</div>
						{errors.active && (
							<p className="text-red-500 text-sm mt-1">
								{errors.active.message}
							</p>
						)}
					</div>
				</div>

				<div className="w-full flex justify-end mt-8">
					<OrangeButton
						type="submit"
						title={
							uploading
								? "Uploading..."
								: isEditForm
									? "Update Employee Bonus"
									: "Add Employee Bonus"
						}
						className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
						disabled={uploading}
					/>
				</div>
			</form>
		</ModalComponent>
	);
};

export default EmployeeBonusModalForm;
